import { useEffect, useState } from 'react'
import { APP_VERSION } from '@/app/version'
import { PrimaryButton, SecondaryButton, useNotifications } from '@/components'
import { activateWaitingWorker, registerPwa, type PwaUpdateInfo } from '@/services/pwa'
import { prepareForAppUpdate } from '@/services/updateSafety'

export function PwaUpdatePrompt() {
  const [update, setUpdate] = useState<PwaUpdateInfo>()
  const [preparing, setPreparing] = useState(false)
  const { notify } = useNotifications()

  useEffect(() => {
    let active = true
    let disposeRegistration: (() => void) | undefined
    void registerPwa({
      onOfflineReady: () => {
        if (!active) return
        notify({
          title: 'Aplicativo preparado',
          message: 'A aplicação e seus recursos locais já podem abrir sem internet.',
          tone: 'success',
        })
      },
      onUpdateAvailable: (availableUpdate) => {
        if (active) setUpdate(availableUpdate)
      },
      onError: () => {
        if (!active) return
        notify({
          title: 'Atualização indisponível',
          message: 'A versão atual continua funcionando. Tente atualizar quando houver conexão.',
          tone: 'warning',
        })
      },
    }).then((handle) => {
      if (!handle) return
      if (active) {
        disposeRegistration = handle.dispose
      } else {
        handle.dispose()
      }
    })
    return () => {
      active = false
      disposeRegistration?.()
    }
  }, [notify])

  const updateNow = async () => {
    if (!update || preparing) return
    setPreparing(true)
    const result = await prepareForAppUpdate()
    if (!result.safe) {
      notify({
        title: 'Atualização aguardando',
        message: `${result.reason ?? 'Conclua a operação atual.'} Nada foi recarregado.`,
        tone: 'warning',
      })
      setPreparing(false)
      return
    }
    if (!activateWaitingWorker(update.registration)) {
      notify({
        title: 'Atualização adiada',
        message: 'A nova versão deixou de estar pronta. O aplicativo verificará novamente.',
        tone: 'warning',
      })
      setUpdate(undefined)
      setPreparing(false)
    }
  }

  if (!update) return null

  return (
    <section className="update-prompt" aria-label="Atualização disponível" aria-live="polite">
      <div>
        <strong>Nova versão disponível</strong>
        <p>
          {preparing
            ? 'Salvando alterações antes de atualizar…'
            : `Versão atual ${APP_VERSION}${update.version ? ` · nova ${update.version}` : ''}. A atualização preserva o banco local.`}
        </p>
      </div>
      <div className="update-prompt__actions">
        <SecondaryButton disabled={preparing} onClick={() => setUpdate(undefined)}>
          Depois
        </SecondaryButton>
        <PrimaryButton disabled={preparing} onClick={() => void updateNow()}>
          {preparing ? 'Preparando…' : 'Atualizar agora'}
        </PrimaryButton>
      </div>
    </section>
  )
}
